import { scrollToSection } from '../utils/animations';
import { getImageUrl } from '../utils/images';

/**
 * Footer Component
 *
 * Footer for KimRose Center website with branding and links.
 *
 * Features:
 * - Deep forest green background
 * - 3-column layout (responsive)
 * - Brand tagline in italic font
 * - Quick links navigation
 * - Social media placeholder section
 * - Copyright information
 */
export const Footer = () => {
  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string
  ) => {
    e.preventDefault();
    scrollToSection(sectionId, 80);
  };

  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (window.location.pathname !== '/') {
      window.location.href = '/';
    }
  };

  return (
    <footer className="bg-deep-forest text-white py-12">
      <div className="container mx-auto px-4">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Column 1: Brand */}
          <div>
            {/* Logo in white box */}
            <div className="bg-white rounded-lg p-4 inline-block mb-4">
              <img
                src={getImageUrl('/images/kimrose_logo_transparent-2.png')}
                alt="KimRose Center Logo"
                className="h-20 w-auto"
              />
            </div>
            <h3 className="text-xl font-display font-bold mb-3">
              KimRose Center
            </h3>
            <p className="text-gray-300 font-accent italic text-sm">
              Planted in Love, Watered in Science, Growing in Uniqueness
            </p>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-3">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <a
                  href="/"
                  onClick={handleHomeClick}
                  className="text-gray-300 hover:text-golden-sand transition-colors focus:outline-none focus:ring-2 focus:ring-golden-sand focus:ring-offset-2 focus:ring-offset-deep-forest rounded px-1"
                >
                  Home
                </a>
              </li>
              <li>
                <a
                  href="#about-section"
                  onClick={(e) => handleNavClick(e, 'about-section')}
                  className="text-gray-300 hover:text-golden-sand transition-colors focus:outline-none focus:ring-2 focus:ring-golden-sand focus:ring-offset-2 focus:ring-offset-deep-forest rounded px-1"
                >
                  About
                </a>
              </li>
              <li>
                <a
                  href="#services-section"
                  onClick={(e) => handleNavClick(e, 'services-section')}
                  className="text-gray-300 hover:text-golden-sand transition-colors focus:outline-none focus:ring-2 focus:ring-golden-sand focus:ring-offset-2 focus:ring-offset-deep-forest rounded px-1"
                >
                  Services
                </a>
              </li>
              <li>
                <a
                  href="#contact-section"
                  onClick={(e) => handleNavClick(e, 'contact-section')}
                  className="text-gray-300 hover:text-golden-sand transition-colors focus:outline-none focus:ring-2 focus:ring-golden-sand focus:ring-offset-2 focus:ring-offset-deep-forest rounded px-1"
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Column 3: Connect */}
          <div>
            <h4 className="text-lg font-semibold mb-3">Connect With Us</h4>
            <p className="text-gray-300 text-sm mb-3">
              Follow us on social media for updates and inspiration
            </p>
            {/* Social media icons placeholder - will be added in Phase 2 */}
            <div className="flex space-x-4">
              {/* Placeholder for future social media icons */}
              <div className="text-gray-400 text-sm italic">Coming soon</div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-600 pt-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-gray-400 text-sm">
              &copy; {new Date().getFullYear()} KimRose Center. All rights reserved.
            </p>

            {/* Designer Credit */}
            <a
              href="https://kgprodesign.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-500 hover:text-gray-300 transition-colors group"
            >
              <span className="text-xs">Designed by</span>
              <img
                src={getImageUrl('/images/KGProDesign_LOGO.png')}
                alt="KGProDesign"
                className="h-5 w-auto opacity-70 group-hover:opacity-100 transition-opacity"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}; 